import { useState } from "react"
import { IssueStatus, IssuePriority } from "~/lib/enums"
import { textToEditorContent, markdownToEditorContent } from "~/lib/utils"
import { authClient } from "~/lib/auth-client"
import { tutorials } from "~/lib/tutorials"
import { defaultDifficulties } from "~/lib/difficulty"
import type { RouterInputs, RouterOutputs } from "~/trpc/react"

const practiceContent = tutorials["onboarding-v1"].practice
const createdAt = new Date()
const sampleUser = {
	id: "practice-user",
	name: "",
	email: "practice@example.invalid",
	emailVerified: false,
	image: null,
	createdAt,
	updatedAt: createdAt,
	role: null,
	banned: false,
	banReason: null,
	banExpires: null,
	lastLoginMethod: null,
	lastSeenAt: null,
	timezone: null,
	themePreference: null,
	defaultView: "issues",
	lastVisitedOrganizationId: null,
	globalHideTutorials: false,
	tutorialHistory: {}
}
const sampleMember = {
	id: "practice-member",
	organizationId: "practice-org",
	userId: sampleUser.id,
	fakeRole: "member",
	lastVisitedTeamId: null,
	deletedAt: null,
	createdAt,
	updatedAt: createdAt,
	user: sampleUser
}
const kevin = {
	...sampleMember,
	id: "practice-kevin",
	userId: "practice-kevin-user",
	user: { ...sampleUser, id: "practice-kevin-user", name: "Kevin" }
}
const initialComment: NonNullable<
	RouterOutputs["issues"]["get"]
>["comments"][number] = {
	id: "practice-comment",
	sourceEventId: "practice-comment-event",
	content: textToEditorContent(practiceContent.comment),
	githubCommentId: null,
	githubCommentUrl: null,
	isResolved: false,
	resolvedAt: null,
	issueId: "practice-issue",
	repliesToId: null,
	createdAt,
	updatedAt: createdAt,
	orgMemberId: kevin.id,
	resolvedByOrgMemberId: null,
	sourceEvent: {
		id: "practice-comment-event",
		issueId: "practice-issue",
		type: "Comment",
		oldValue: null,
		newValue: null,
		actorOverrideName: null,
		actorOverrideImage: null,
		actorIsSystem: false,
		actorOrgMemberId: kevin.id,
		createdAt,
		updatedAt: createdAt
	},
	reactions: [],
	member: kevin,
	resolvedBy: null
}
export const onboardingDescription = markdownToEditorContent(
	practiceContent.issueDescription
)
export const onboardingArticle = markdownToEditorContent(
	practiceContent.articleContent
)

export function useOnboardingData(
	tutorialId: "onboarding-v1" | "queue-v1" = "onboarding-v1"
) {
	const content = tutorials[tutorialId].practice
	const [reviewOpen, setReviewOpen] = useState(false)
	const [queueDecision, setQueueDecision] = useState<"accept" | "decline">()
	const { data: session } = authClient.useSession()
	const member = {
		...sampleMember,
		userId: session?.user.id ?? sampleUser.id,
		user: {
			...sampleUser,
			id: session?.user.id ?? sampleUser.id,
			name: session?.user.name ?? "",
			image: session?.user.image ?? null
		}
	}
	const [issue, setIssue] = useState<
		Pick<
			NonNullable<RouterOutputs["issues"]["get"]>,
			| "id"
			| "friendlyId"
			| "teamId"
			| "title"
			| "description"
			| "status"
			| "priority"
			| "difficulty"
			| "dueDate"
			| "assignee"
			| "labels"
			| "events"
			| "comments"
			| "currentUserDraft"
			| "currentOrgMemberId"
			| "memberIdToNameMap"
			| "teamLabels"
			| "projectId"
			| "githubRepo"
			| "githubIssueNumber"
			| "pullRequests"
			| "updatedAt"
		>
	>(() => ({
		id: "practice-issue",
		friendlyId: tutorialId === "queue-v1" ? 2 : 1,
		teamId: "practice-team",
		title: content.issueTitle,
		description: markdownToEditorContent(content.issueDescription),
		status: tutorialId === "queue-v1" ? IssueStatus.Queue : IssueStatus.ToDo,
		priority:
			tutorialId === "queue-v1" ? IssuePriority.High : IssuePriority.Medium,
		difficulty: null,
		dueDate: null,
		assignee: null,
		labels: [],
		events: [],
		comments: [
			{ ...initialComment, content: textToEditorContent(content.comment) }
		],
		currentUserDraft: undefined,
		currentOrgMemberId: member.id,
		memberIdToNameMap: [member, kevin].map((entry) => ({
			id: entry.id,
			orgMemberId: entry.id,
			userId: entry.userId,
			name: entry.user.name,
			image: entry.user.image,
			lastSeenAt: null,
			timezone: null,
			teamRoles: [],
			teams: []
		})),
		teamLabels: [],
		projectId: null,
		githubRepo: null,
		githubIssueNumber: null,
		pullRequests: [],
		updatedAt: new Date()
	}))
	const [document, setDocument] = useState({
		id: "practice-document",
		title: practiceContent.articleTitle,
		description: practiceContent.articleSummary,
		category: practiceContent.articleCategory,
		icon: "BookOpen",
		content: onboardingArticle as string | null,
		accessRoles: [] as { id: string }[],
		updatedAt: new Date(),
		pinned: false,
		userHasFavorited: false,
		accessPolicy: {
			allTeamMembers: true,
			teamRoles: [] as string[],
			allTeamMembersAreAdmins: false,
			teamAdminRoles: [] as string[],
			allOrganizationMembersAreAdmins: false,
			organizationAdminRoles: [] as string[]
		}
	})
	const edit = async (change: RouterInputs["issues"]["edit"]) => {
		const now = new Date()
		setIssue((current) => {
			const next = { ...current, updatedAt: now }
			const events = [...current.events]
			for (const [field, type] of [
				["status", "StatusChange"],
				["priority", "PriorityChange"],
				["difficulty", "DifficultyChange"],
				["dueDate", "DueDateChange"],
				["title", "TitleChange"],
				["description", "DescriptionChange"]
			] as const) {
				if (
					change[field] === undefined ||
					JSON.stringify(change[field]) === JSON.stringify(current[field])
				)
					continue
				Object.assign(next, { [field]: change[field] })
				events.push({
					id: crypto.randomUUID(),
					type,
					oldValue: { [field]: current[field] },
					newValue: { [field]: change[field] },
					issueId: current.id,
					actorOverrideName: null,
					actorOverrideImage: null,
					actorIsSystem: false,
					actorOrgMemberId: member.id,
					createdAt: now,
					updatedAt: now,
					actor: member,
					comment: null
				})
			}
			return { ...next, events }
		})
		return { id: issue.id }
	}
	return {
		// Resolve the practice actor against the current session, including activity
		// already created before a profile/session refresh.
		issue: {
			...issue,
			memberIdToNameMap: issue.memberIdToNameMap.map((entry) =>
				entry.id === member.id
					? {
							...entry,
							userId: member.userId,
							name: member.user.name,
							image: member.user.image
						}
					: entry
			),
			events: issue.events.map((event) =>
				event.actorOrgMemberId === member.id
					? { ...event, actor: member }
					: event
			),
			comments: issue.comments.map((comment) => ({
				...comment,
				member: comment.orgMemberId === member.id ? member : comment.member,
				resolvedBy:
					comment.resolvedByOrgMemberId === member.id
						? member
						: comment.resolvedBy,
				reactions: comment.reactions.map((reaction) =>
					reaction.orgMemberId === member.id
						? { ...reaction, member }
						: reaction
				)
			}))
		},
		reviewOpen,
		setReviewOpen,
		queueDecision,
		setQueueDecision,
		document,
		team: {
			id: "practice-team",
			name: practiceContent.teamName,
			slug: practiceContent.teamSlug,
			logo: null,
			enableProjects: false,
			difficulties: defaultDifficulties,
			roles: []
		},
		edit,
		comment: async (content: string, repliesToId: string | null = null) => {
			const now = new Date()
			setIssue((current) => ({
				...current,
				comments: [
					...current.comments,
					{
						...initialComment,
						id: crypto.randomUUID(),
						sourceEventId: crypto.randomUUID(),
						content,
						repliesToId,
						member,
						orgMemberId: member.id,
						createdAt: now,
						updatedAt: now
					}
				]
			}))
		},
		resolve: (commentId: string, isResolved: boolean) =>
			setIssue((current) => ({
				...current,
				comments: current.comments.map((comment) =>
					comment.id !== commentId
						? comment
						: {
								...comment,
								isResolved,
								resolvedAt: isResolved ? new Date() : null,
								resolvedByOrgMemberId: isResolved ? member.id : null,
								resolvedBy: isResolved ? member : null
							}
				)
			})),
		react: (
			commentId: string,
			type: RouterInputs["issues"]["toggleCommentReaction"]["type"]
		) =>
			setIssue((current) => ({
				...current,
				comments: current.comments.map((comment) => {
					if (comment.id !== commentId) return comment
					const exists = comment.reactions.some(
						(reaction) =>
							reaction.type === type && reaction.orgMemberId === member.id
					)
					return {
						...comment,
						reactions: exists
							? comment.reactions.filter(
									(reaction) =>
										reaction.type !== type || reaction.orgMemberId !== member.id
								)
							: [
									...comment.reactions,
									{
										id: crypto.randomUUID(),
										type,
										commentId,
										orgMemberId: member.id,
										member,
										createdAt: new Date(),
										updatedAt: new Date()
									}
								]
					}
				})
			})),
		editDocument: async (change: RouterInputs["knowledgeBase"]["edit"]) => {
			const updatedAt = new Date()
			setDocument((current) => ({
				...current,
				title: change.title ?? current.title,
				description: change.description ?? "",
				category: change.category ?? "",
				icon: change.icon ?? current.icon,
				content: change.content ?? null,
				updatedAt
			}))
			return { updatedAt }
		}
	}
}
